<template>
  <validation-observer ref="billingObserver">
    <div class="billing-form">
      <!-- Account type toggle: pretty 2-card switch instead of radio buttons -->
      <div v-if="showTypeToggle" class="account-type-toggle mb-1">
        <button
          type="button"
          class="acct-card"
          :class="{ active: local.account_type === 'personal' }"
          @click="setType('personal')"
        >
          <feather-icon icon="UserIcon" size="20" />
          <span class="acct-title">Individual</span>
          <span class="acct-sub">Personal invoice</span>
        </button>
        <button
          type="button"
          class="acct-card"
          :class="{ active: local.account_type === 'business' }"
          @click="setType('business')"
        >
          <feather-icon icon="BriefcaseIcon" size="20" />
          <span class="acct-title">Business</span>
          <span class="acct-sub">Company / VAT</span>
        </button>
      </div>

      <!-- Country (always required) -->
      <b-form-group label="Country" label-for="bill-country" class="mb-1">
        <validation-provider
          #default="{ errors, validated }"
          name="Country"
          rules="required"
          mode="passive"
        >
          <v-select
            id="bill-country"
            v-model="local.country"
            :options="allCountries"
            :clearable="false"
            :reduce="opt => opt"
            placeholder="Select country"
            @input="onCountryChange"
          />
          <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

        <!-- Business: company name (always shown for business) -->
        <b-form-group
          v-if="local.account_type === 'business'"
          label="Company name"
          label-for="bill-company"
          class="mb-1"
        >
          <validation-provider
            #default="{ errors, validated }"
            name="Company name"
            rules="required|min:2"
            mode="passive"
          >
            <b-form-input
              id="bill-company"
              v-model="local.company_name"
              placeholder="ACME GmbH"
              :state="validated && errors.length ? false : null"
            />
            <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Business: Tax / VAT ID (single field — VIES-validated when EU country) -->
        <b-form-group
          v-if="local.account_type === 'business'"
          label-for="bill-tax-id"
          class="mb-1"
        >
          <template #label>
            <span>Tax / VAT ID</span>
            <b-spinner
              v-if="vies.checking"
              small
              variant="primary"
              class="ml-50"
            />
            <feather-icon
              v-else-if="vies.valid === true"
              icon="CheckCircleIcon"
              class="text-success ml-50"
              size="14"
            />
            <feather-icon
              v-else-if="vies.transient"
              icon="AlertCircleIcon"
              class="text-warning ml-50"
              size="14"
            />
            <feather-icon
              v-else-if="vies.valid === false"
              icon="XCircleIcon"
              class="text-danger ml-50"
              size="14"
            />
          </template>
          <validation-provider
            #default="{ errors, validated }"
            name="Tax / VAT ID"
            rules="required|min:3"
            mode="passive"
          >
            <b-form-input
              id="bill-tax-id"
              :value="local.tax_id"
              :placeholder="taxIdPlaceholder"
              :state="
                (validated && errors.length) || (vies.valid === false && !vies.transient)
                  ? false
                  : vies.valid === true
                  ? true
                  : null
              "
              @input="onTaxIdInput"
            />
            <small v-if="validated && errors.length" class="text-danger">{{ errors[0] }}</small>
            <div v-else-if="vies.valid === true" class="vies-confirmed">
              <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
              <strong>Verified by VIES</strong>
              <template v-if="vies.companyName"> — {{ vies.companyName }}</template>
              <template v-if="isReverseChargeEligible">
                <span class="vc-tag vc-success">Reverse charge applies (0% VAT)</span>
              </template>
              <template v-else-if="local.country === 'Romania'">
                <span class="vc-tag vc-info">Same-country B2B — 21% RO VAT</span>
              </template>
            </div>
            <small v-else-if="vies.transient" class="text-warning">
              {{ friendlyViesError(vies.error) || "Couldn't verify with VIES right now — try again in a moment. You can still save your details." }}
            </small>
            <small v-else-if="vies.valid === false && isEUCountry" class="text-warning">
              {{ friendlyViesError(vies.error) || "Not registered in VIES — saved as Tax ID (no reverse charge applies)." }}
            </small>
            <small v-else-if="vies.error" class="text-muted">
              {{ friendlyViesError(vies.error) }}
            </small>
            <small v-else class="text-muted">
              <template v-if="isEUCountry">
                Your tax or VAT number — verified live via VIES if it's an EU VAT ID.
                Otherwise stored as Tax ID (e.g. CUI for non-VAT, Steuernummer, etc.).
              </template>
              <template v-else>
                Your company's tax/registration number — appears on the invoice.
              </template>
            </small>
          </validation-provider>
        </b-form-group>

        <!-- VIES data suggestion (name + address; DE/IT skip due to local privacy rules) -->
        <div
          v-if="local.account_type === 'business' && isEUCountry && vies.valid && !viesApplied && (viesAddress || vies.companyName)"
          class="vies-suggestion mb-1"
        >
          <div class="vs-header">
            <feather-icon icon="CheckCircleIcon" size="14" class="text-success mr-50" />
            <strong class="text-success">VIES data on record</strong>
          </div>
          <div v-if="vies.companyName" class="mb-50">
            <small class="text-muted d-block">Registered name</small>
            <strong>{{ vies.companyName }}</strong>
          </div>
          <div v-if="viesAddress">
            <small class="text-muted d-block">Registered address</small>
            <pre class="vs-body">{{ viesAddress }}</pre>
          </div>
          <div v-if="isReverseChargeEligible" class="vs-eligibility mb-50">
            <feather-icon icon="ShieldIcon" size="13" class="mr-25" />
            <strong>Reverse charge applies</strong> — your invoices will be issued
            with 0% VAT under Article 196 of EU Directive 2006/112/EC.
          </div>
          <div v-else-if="vies.valid && local.country === 'Romania'" class="vs-eligibility-info mb-50">
            <feather-icon icon="InfoIcon" size="13" class="mr-25" />
            <strong>VIES verified.</strong> Same-country B2B — invoices include
            standard 21% RO VAT (reverse charge does not apply between Romanian
            companies).
          </div>
          <b-button variant="outline-success" size="sm" @click="applyViesData">
            <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
            Use VIES data
          </b-button>
        </div>

        <!-- Business + non-EU: helpful note that VAT is not collected -->
        <b-alert
          v-if="local.account_type === 'business' && local.country && !isEUCountry"
          variant="info"
          show
          class="my-1 py-50 px-1"
        >
          <small>
            <feather-icon icon="GlobeIcon" size="14" class="mr-50" />
            Non-EU country — invoice issued as export of services. VAT ID not needed.
          </small>
        </b-alert>

        <!-- Personal-only field -->
        <b-form-group
          v-if="local.account_type === 'personal'"
          label="Full name on invoice"
          label-for="bill-name"
          class="mb-1"
        >
          <validation-provider
            #default="{ errors, validated }"
            name="Full name"
            rules="required|min:2"
            mode="passive"
          >
            <b-form-input
              id="bill-name"
              v-model="local.full_name"
              placeholder="John Doe"
              :state="validated && errors.length ? false : null"
            />
            <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Address (single field — full street/city/postal code in one go) -->
        <b-form-group label="Address" label-for="bill-addr" class="mb-1">
          <validation-provider
            #default="{ errors, validated }"
            name="Address"
            rules="required|min:5"
            mode="passive"
          >
            <b-form-textarea
              id="bill-addr"
              v-model="local.address"
              placeholder="Street, city, postal code"
              rows="2"
              max-rows="4"
              :state="validated && errors.length ? false : null"
            />
            <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Romania only: structured city + county, required by ANAF e-Factura
             (invoices to Romanian clients are rejected without a real county;
             Bucharest must use the sector as the city, e.g. "Sector 3"). -->
        <template v-if="isRomania">
          <b-form-group label="City (Oraș)" label-for="bill-city" class="mb-1">
            <validation-provider
              #default="{ errors, validated }"
              name="City"
              rules="required|min:2"
              mode="passive"
            >
              <b-form-input
                id="bill-city"
                v-model="local.city"
                placeholder="ex. Focșani (București: Sector 1–6)"
                :state="validated && errors.length ? false : null"
              />
              <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="County (Județ)" label-for="bill-county" class="mb-1">
            <validation-provider
              #default="{ errors, validated }"
              name="County"
              rules="required"
              mode="passive"
            >
              <v-select
                id="bill-county"
                v-model="local.county"
                :options="roCounties"
                :clearable="false"
                placeholder="Selectează județul"
                :class="validated && errors.length ? 'is-invalid' : ''"
              />
              <small v-if="validated" class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </template>
    </div>
  </validation-observer>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BAlert,
  BLink,
  BSpinner,
  BButton,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { required, min } from "@validations";
import countries from "@/libs/countries.js";
import EU_COUNTRIES, { isEU, euInfo } from "@/libs/eu-countries.js";
import Billing from "@/services/billing.service.js";

// VIES sometimes returns "---" for name/address (DE/IT have privacy restrictions).
// Treat those as unavailable so the UI doesn't auto-fill garbage.
const isUseful = (s) => typeof s === "string" && s.trim() !== "" && !/^-+$/.test(s.trim());

// Normalize any tax/VAT ID before it leaves the form: strip all whitespace and
// uppercase. Applies to every country, not just RO — e.g. "de 143 593 636" →
// "DE143593636", "ro11062173" → "RO11062173". This is what eventually shows up
// on the PDF, in the UBL CompanyID, and in the SAGA <ClientCIF> tag, so we keep
// formats consistent across all three. Avoids ANAF/CIUS-RO validation errors
// like BR-CO-09 / BR-RO-007 that fail on lowercase RO prefixes.
const normalizeTaxId = (s) => (s == null ? "" : String(s).replace(/\s+/g, "").toUpperCase());

const empty = () => ({
  account_type: "personal",
  full_name: "",
  company_name: "",
  tax_id: "",
  country: "",
  address: "",
  // Romania only — structured locality/county for ANAF e-Factura (SmartBill)
  city: "",
  county: "",
});

// Official RO counties (no diacritics — keeps SmartBill's SAGA export and the
// legacy parsers happy). "Bucuresti" requires the sector in the City field.
const RO_COUNTIES = [
  "Alba", "Arad", "Arges", "Bacau", "Bihor", "Bistrita-Nasaud", "Botosani",
  "Braila", "Brasov", "Bucuresti", "Buzau", "Calarasi", "Caras-Severin",
  "Cluj", "Constanta", "Covasna", "Dambovita", "Dolj", "Galati", "Giurgiu",
  "Gorj", "Harghita", "Hunedoara", "Ialomita", "Iasi", "Ilfov", "Maramures",
  "Mehedinti", "Mures", "Neamt", "Olt", "Prahova", "Salaj", "Satu Mare",
  "Sibiu", "Suceava", "Teleorman", "Timis", "Tulcea", "Valcea", "Vaslui",
  "Vrancea",
];

export default {
  name: "BillingDetailsForm",
  components: {
    ValidationProvider,
    ValidationObserver,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BAlert,
    BLink,
    BSpinner,
    BButton,
    vSelect,
  },
  props: {
    value: { type: Object, default: () => empty() },
    showTypeToggle: { type: Boolean, default: true },
    contactEmail: {
      type: String,
      default: process.env.VUE_MAIL_CONTACT || "support@imeicheck.com",
    },
  },
  data() {
    const initial = { ...empty(), ...(this.value || {}) };
    // Normalize tax_id on first load too — older records may have been saved
    // before live normalization existed (e.g. "ro11062173" instead of
    // "RO11062173"). Cleans the display the first time the form opens.
    if (initial.tax_id) {
      initial.tax_id = String(initial.tax_id).replace(/\s+/g, "").toUpperCase();
    }
    return {
      local: initial,
      allCountries: countries,
      vies: { checking: false, valid: null, companyName: null, error: null, transient: false },
      viesAddress: null, // raw multi-line string returned by VIES (when available)
      viesApplied: false, // suppresses the suggestion box after user clicked Apply
      viesTimer: null,
      required,
      min,
    };
  },
  computed: {
    isEUCountry() {
      return isEU(this.local.country);
    },
    isRomania() {
      return this.local.country === "Romania";
    },
    roCounties() {
      return RO_COUNTIES;
    },
    /**
     * Reverse charge only applies when buyer is in another EU country (not the
     * seller's). For our seller (RO), a Romanian buyer gets standard RO VAT,
     * not reverse charge — even if their VAT ID is VIES-valid.
     */
    isReverseChargeEligible() {
      return (
        this.local.account_type === "business" &&
        this.isEUCountry &&
        this.vies.valid === true &&
        this.local.country !== "Romania"
      );
    },
    /**
     * Country-aware placeholder for the Tax / VAT ID field.
     */
    taxIdPlaceholder() {
      if (this.isEUCountry) return "RO48037195 / DE143593636 / etc.";
      const c = this.local.country || "";
      const map = {
        "United States of America (the)": "EIN — e.g. 12-3456789",
        "United Kingdom": "Company Number / VAT — e.g. GB123456789",
        "Canada": "Business Number — e.g. 123456789RT0001",
        "Australia": "ABN — e.g. 12 345 678 901",
        "New Zealand": "NZBN / IRD number",
        "Switzerland": "UID / CHE-XXX.XXX.XXX",
        "Norway": "Org. nr. — e.g. 123 456 789 MVA",
        "Singapore": "UEN — e.g. 201912345A",
        "India": "GSTIN / PAN",
      };
      return map[c] || "Tax ID / EIN / Company Number / etc.";
    },
    payload() {
      const eu = euInfo(this.local.country);
      const viesOk = this.vies.valid === true;
      const taxId = normalizeTaxId(this.local.tax_id);
      return {
        ...this.local,
        // tax_id is uppercased + whitespace-stripped so it lands the same in
        // PDF, UBL XML and SAGA XML regardless of how the user typed it.
        tax_id: taxId,
        // When VIES validated successfully, the tax_id IS the VAT ID — store it
        // in both columns. Otherwise vat_id stays empty (no reverse charge).
        vat_id: viesOk ? taxId : "",
        country_code: eu ? eu.code : null,
        is_eu: !!eu,
        vies_valid: viesOk,
        vies_state: this.vies.valid,         // true | false | null (not checked)
        vies_checking: this.vies.checking,   // true while VIES request in flight
      };
    },
  },
  watch: {
    payload: {
      handler(p, prev) {
        if (prev && JSON.stringify(prev) === JSON.stringify(p)) return;
        this.$emit("input", p);
        this.$emit("change", p);
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.viesTimer);
  },
  methods: {
    setType(type) {
      if (this.local.account_type === type) return;
      this.local.account_type = type;
      this.vies = { checking: false, valid: null, companyName: null, error: null, transient: false };
      this.viesAddress = null;
      if (type === "personal") {
        this.local.company_name = "";
        this.local.tax_id = "";
      }
    },
    onCountryChange() {
      this.vies = { checking: false, valid: null, companyName: null, error: null, transient: false };
      this.viesAddress = null;
      // City/county are Romania-only fields — clear them so a user who
      // switches away from Romania doesn't submit stale RO locality data.
      if (this.local.country !== "Romania") {
        this.local.city = "";
        this.local.county = "";
      }
      if (this.local.tax_id) this.debouncedValidateVAT();
    },
    /**
     * Live-normalize the Tax/VAT ID field on every keystroke and paste:
     * uppercase + strip all whitespace. Applies to every country (RO, DE, GB,
     * US, …). What the user sees in the input is always the form that lands in
     * the PDF / UBL `<cbc:CompanyID>` / SAGA `<ClientCIF>` — no surprise drift
     * between display and stored value, no late surprise normalization.
     *
     * The `if (norm !== ...)` guard avoids re-setting the model on a no-op
     * input (e.g. user types an already-uppercase character) which would
     * otherwise reset the cursor on some browsers.
     */
    onTaxIdInput(v) {
      const norm = (v == null ? "" : String(v).replace(/\s+/g, "").toUpperCase());
      if (this.local.tax_id !== norm) {
        this.local.tax_id = norm;
      }
      this.debouncedValidateVAT();
    },
    debouncedValidateVAT() {
      clearTimeout(this.viesTimer);
      this.vies = { checking: false, valid: null, companyName: null, error: null, transient: false };
      this.viesApplied = false; // re-typing VAT ID re-arms the suggestion box

      // If user pasted "IE6388047V" while country is RO, auto-switch country.
      this.maybeSyncCountryFromPrefix();

      if (
        this.local.account_type !== "business" ||
        !this.isEUCountry ||
        !this.local.tax_id ||
        this.local.tax_id.length < 4
      ) {
        return;
      }
      this.viesTimer = setTimeout(this.validateVAT, 600);
    },
    /**
     * Detect a 2-letter EU country prefix in tax_id and auto-update the
     * Country dropdown to match (so VIES validates against the right state).
     * Example: pasting "IE6388047V" while country=Romania → switches to Ireland.
     */
    maybeSyncCountryFromPrefix() {
      const id = (this.local.tax_id || "").toUpperCase().replace(/\s+/g, "");
      if (id.length < 3) return;
      const prefix = id.slice(0, 2);
      const matched = EU_COUNTRIES.find(
        (c) => c.viesCode === prefix || c.code === prefix
      );
      if (!matched) return;
      if (this.local.country === matched.name) return;
      this.local.country = matched.name;
      // Programmatic country change bypasses the v-select @input handler, so
      // apply the same Romania-only cleanup here: otherwise a stale RO
      // city/county would leak onto a non-RO invoice payload.
      if (matched.name !== "Romania") {
        this.local.city = "";
        this.local.county = "";
      }
    },
    async validateVAT() {
      const eu = euInfo(this.local.country);
      if (!eu) return;
      this.vies.checking = true;
      try {
        const result = await Billing.validateVAT({
          country: eu.viesCode,
          vat_id: this.local.tax_id,
        });
        const usefulName    = isUseful(result.name);
        const usefulAddress = isUseful(result.address);
        this.vies = {
          checking: false,
          valid: !!result.valid,
          companyName: usefulName ? result.name : null,
          error: result.valid ? null : result.error || "VAT ID not valid",
          transient: !!result.transient,
        };
        this.viesAddress = result.valid && usefulAddress ? result.address : null;
        // Auto-fill company name from VIES — it's the legally registered name,
        // safer to use than whatever the user typed.
        if (result.valid && usefulName) {
          this.local.company_name = result.name;
        }
      } catch (e) {
        const status = e?.response?.status;
        // 401 = auth issue (token expired). Don't blame VIES — the call never reached it.
        if (status === 401 || status === 419) {
          this.vies = {
            checking: false,
            valid: null,
            companyName: null,
            error: "Please re-login to validate VAT IDs.",
            transient: false,
          };
        } else {
          // True network/CORS/timeout failure — let user save anyway.
          this.vies = {
            checking: false,
            valid: null,
            companyName: null,
            error: "Couldn't reach the validator — try again in a moment.",
            transient: true,
          };
        }
        this.viesAddress = null;
      }
    },
    /**
     * Apply VIES data to the form: company name + address.
     * Always overwrites — click = explicit intent to use VIES data.
     * Trailing country line is stripped from address (already known from the country dropdown).
     * After apply, the suggestion box hides — user can re-trigger by retyping VAT ID.
     */
    applyViesData() {
      if (this.vies.companyName) {
        this.local.company_name = this.vies.companyName;
      }
      if (this.viesAddress) {
        const lines = this.viesAddress
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);
        if (lines.length > 1 && /^[a-zA-ZÀ-ž\s\-]+$/.test(lines[lines.length - 1])) {
          lines.pop();
        }
        this.local.address = lines.join(", ");
      }
      this.viesApplied = true;
    },
    /**
     * Translate cryptic VIES error codes (returned verbatim from EU's REST API)
     * into human-readable text. Returns null when there is no error worth showing.
     *
     * Codes seen in the wild:
     *   INVALID                       — VAT ID not registered in VIES (most common false positive)
     *   MS_MAX_CONCURRENT_REQ         — that member state's VIES gateway is rate-limiting
     *   MS_UNAVAILABLE / TIMEOUT      — member state is offline temporarily
     *   SERVER_BUSY / SERVICE_UNAVAILABLE — VIES global hub overloaded
     *   GLOBAL_MAX_CONCURRENT_REQ     — VIES-wide rate limit
     */
    friendlyViesError(err) {
      if (!err) return null;
      const e = String(err).toUpperCase();
      if (e === "INVALID" || /NOT[_\s]FOUND|NOT[_\s]VALID/.test(e)) {
        return "This VAT ID isn't registered in VIES. You can still save it as a Tax ID — reverse charge won't apply.";
      }
      if (/MAX_CONCURRENT_REQ|GLOBAL_MAX/.test(e)) {
        return "VIES is rate-limiting us right now — try again in a minute. You can still save your details.";
      }
      if (/MS_UNAVAILABLE|SERVER_BUSY|SERVICE_UNAVAILABLE|TIMEOUT/.test(e)) {
        return "That country's VAT registry is temporarily down — try again in a minute. You can still save your details.";
      }
      // Backend's own friendly strings already pass through unchanged
      return err;
    },
    async validate() {
      const ok = await this.$refs.billingObserver.validate();
      return ok;
    },
    reset() {
      this.local = empty();
      this.vies = { checking: false, valid: null, companyName: null, error: null };
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
<style lang="scss" scoped>
.billing-form {
  .account-type-toggle {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;

    .acct-card {
      background: transparent;
      border: 1px solid var(--bs-border-color, #d8d6de);
      border-radius: 0.428rem;
      padding: 0.85rem 0.75rem;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;
      color: inherit;
      transition: all 0.15s ease;

      .acct-title {
        font-weight: 600;
        font-size: 0.95rem;
      }
      .acct-sub {
        font-size: 0.78rem;
        opacity: 0.7;
      }

      &:hover {
        border-color: #7367f0;
      }
      &.active {
        border-color: #7367f0;
        background: rgba(115, 103, 240, 0.08);
        color: #7367f0;
      }
    }
  }

  .form-group {
    margin-bottom: 0.85rem;
  }

  .vies-suggestion {
    background: rgba(40, 199, 111, 0.08);
    border: 1px solid rgba(40, 199, 111, 0.25);
    border-radius: 0.428rem;
    padding: 0.65rem 0.85rem;

    .vs-header {
      display: flex;
      align-items: center;
      font-size: 0.85rem;
      margin-bottom: 0.25rem;
    }
    .vs-body {
      font-family: inherit;
      font-size: 0.85rem;
      margin: 0 0 0.5rem 0;
      white-space: pre-wrap;
      opacity: 0.85;
    }
    .vs-eligibility {
      font-size: 0.78rem;
      color: #28c76f;
      background: rgba(40, 199, 111, 0.12);
      border-radius: 0.3rem;
      padding: 0.4rem 0.6rem;
    }
    .vs-eligibility-info {
      font-size: 0.78rem;
      color: #00cfe8;
      background: rgba(0, 207, 232, 0.12);
      border-radius: 0.3rem;
      padding: 0.4rem 0.6rem;
    }
  }

  .vies-confirmed {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.35rem;
    padding: 0.35rem 0.6rem;
    background: rgba(40, 199, 111, 0.1);
    border: 1px solid rgba(40, 199, 111, 0.25);
    border-radius: 0.3rem;
    font-size: 0.78rem;
    color: #28c76f;

    .vc-tag {
      font-size: 0.72rem;
      padding: 0.15rem 0.45rem;
      border-radius: 0.2rem;
      font-weight: 600;
      &.vc-success {
        background: #28c76f;
        color: #fff;
      }
      &.vc-info {
        background: #00cfe8;
        color: #fff;
      }
    }
  }
}
</style>
